import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "components/ui/toast";
export const useGetSocialPostsQuery = (params = {}) => {
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_SOCIAL_POST, params],
    queryFn: () => api.get(API_ROUTES.GET_SOCIAL_POST, { params }),
    placeholderData: keepPreviousData,
  });
  return {
    data: data?.data?.data ?? [],
    pagination: data?.data?.pagination ?? null,
    isPending,
    isFetching,
    isError,
    error,
  };
};

export const useGetSocialPostQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_SOCIAL_POST, { id }],
    queryFn: () => api.get(API_ROUTES.GET_SOCIAL_POST + `/${id}`),
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useDeleteSocialPostMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, scope }) =>
        api.delete(`${API_ROUTES.GET_SOCIAL_POST}/${id}`, {
          params: scope ? { scope } : {},
        }),
      onSuccess: (response) => {
        showToast(
          "success",
          response?.data?.message || "Social post deleted",
        );
      },
      onError: (error) => {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to delete social post",
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_SOCIAL_POST]);
      },
    });
  return { mutate, mutateAsync, isError, error, isSuccess, isPending };
};

export const useCancelSocialPostMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, scope }) =>
        api.post(`${API_ROUTES.GET_SOCIAL_POST}/${id}/cancel`, { scope }),
      onSuccess: (response) => {
        showToast(
          "success",
          response?.data?.message || "Social post stopped",
        );
      },
      onError: (error) => {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to stop social post",
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_SOCIAL_POST]);
      },
    });
  return { mutate, mutateAsync, isError, error, isSuccess, isPending };
};

export const useUpdateSocialPostMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, data }) =>
        api.put(`${API_ROUTES.GET_SOCIAL_POST}/${id}`, data),
      onSuccess: (response) => {
        showToast("success", response?.data?.message || "Social post updated");
      },
      onError: (error) => {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to update Social Post"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_SOCIAL_POST]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useGetSocialSubscribersQuery = (params = {}) => {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [API_ROUTES.GET_SOCIAL_SUBSCRIBERS, params],
    queryFn: () => api.get(API_ROUTES.GET_SOCIAL_SUBSCRIBERS, { params }),
    placeholderData: keepPreviousData,
  });
  return { data: data?.data?.data ?? [], isPending, isError, error, refetch };
};

export const useSendSocialReminderMutation = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: ({ userId, type }) =>
      api.post(`${API_ROUTES.SEND_SOCIAL_REMINDER}/${userId}`, { type }),
    onSuccess: () => {
      showToast("success", "Reminder email sent");
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to send reminder"
      );
    },
  });
  return { mutate, isPending };
};

export const useCreateSocialPostMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (formData) =>
        api.post(`${API_ROUTES.GET_SOCIAL_POST}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }),
      onSuccess: (response) => {
        const data = response?.data?.data;
        // The server message carries the held/skipped agents, so it wins when present.
        const message =
          response?.data?.message ||
          (data?.isInstant
            ? `Successfully posted ${data?.postsCreated} posts instantly`
            : `Successfully scheduled ${data?.postsCreated} posts for ${data?.scheduledTime}`);
        showToast(data?.postsCreated ? "success" : "error", message);
      },
      onError: (error) => {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to create social posts"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_SOCIAL_POST]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};
